import { cva } from 'class-variance-authority';

export const validSizes = {
  body: ['sm', 'md', 'lg', 'xl', '2xl'] as const,
  paragraph: ['sm', 'md', 'lg'] as const,
  label: ['sm', 'md', 'lg'] as const,
} as const;

export const validWeights = {
  body: ['bold', 'semibold', 'medium', 'regular'] as const,
  paragraph: ['regular'] as const,
  label: ['regular'] as const,
} as const;

const textRestrictedWeights = {
  body: {
    xl: ['bold', 'regular'],
    '2xl': ['regular'],
  },
} as const;

const fontMap = {
  // body
  'body.2xl.regular':
    'font-(--font-weight-body-2xl) text-(length:--text-body-2xl)',
  'body.xl.bold':
    'font-(--font-weight-body-xl-bold) text-(length:--text-body-xl)',
  'body.xl.regular':
    'font-(--font-weight-body-xl) text-(length:--text-body-xl)',
  'body.lg.bold':
    'font-(--font-weight-body-lg-bold) text-(length:--text-body-lg)',
  'body.lg.medium':
    'font-(--font-weight-body-lg-medium) text-(length:--text-body-lg)',
  'body.lg.regular':
    'font-(--font-weight-body-lg) text-(length:--text-body-lg)',
  'body.md.bold':
    'font-(--font-weight-body-md-bold) text-(length:--text-body-md)',
  'body.md.semibold':
    'font-(--font-weight-body-md-semibold) text-(length:--text-body-md)',
  'body.md.regular':
    'font-(--font-weight-body-md) text-(length:--text-body-md)',
  'body.sm.bold':
    'font-(--font-weight-body-sm-bold) text-(length:--text-body-sm-bold)',
  'body.sm.regular':
    'font-(--font-weight-body-sm) text-(length:--text-body-sm)',
  // paragraph
  'paragraph.lg.regular':
    'font-(--font-weight-paragraph-lg) text-(length:--text-paragraph-lg)',
  'paragraph.md.regular':
    'font-(--font-weight-paragraph-md) text-(length:--text-paragraph-md)',
  'paragraph.sm.regular':
    'font-(--font-weight-paragraph-sm) text-(length:--text-paragraph-sm)',
  // label
  'label.lg.regular':
    'font-(--font-weight-label-lg) text-(length:--text-label-lg)',
  'label.md.regular':
    'font-(--font-weight-label-md) text-(length:--text-label-md)',
  'label.sm.regular':
    'font-(--font-weight-label-sm) text-(length:--text-label-sm)',
} as const;

function getFontClass(
  variant: keyof typeof validSizes,
  size: string,
  weight: string,
) {
  const key = `${variant}.${size}.${weight}` as keyof typeof fontMap;
  return fontMap[key] ?? 'font-(--font-body) text-(length:--text-body-md)';
}

type Variant = keyof typeof validSizes;
type Size<V extends Variant> = (typeof validSizes)[V][number];
type Weight<V extends Variant> = (typeof validWeights)[V][number];

type TextCompoundVariant = {
  variant?: Variant;
  size?: Size<Variant>;
  weight?: Weight<Variant>;
  class: string;
};

export const textCompoundVariants: TextCompoundVariant[] = (
  Object.keys(validSizes) as Variant[]
).flatMap(variant =>
  validSizes[variant].flatMap(size => {
    const restricted = (
      textRestrictedWeights as Record<string, Record<string, readonly string[]>>
    )[variant]?.[size];

    const weights = restricted ?? validWeights[variant];

    return (weights as readonly Weight<typeof variant>[]).map(
      weight =>
        ({
          variant,
          size,
          weight,
          class: getFontClass(variant, size, weight),
        }) as TextCompoundVariant,
    );
  }),
);

export const headingVariants = cva(
  [
    'self-stretch',
    'text-(--color-text-primary)',
    'font-(--font-headings)',
    'mb-(--text-headings-bottom-padding)',
    '[text-transform:var(--text-heading-decoration)]',
    '[font-style:var(--text-heading-style)]',
  ],
  {
    variants: {
      variant: {
        h1: 'text-(length:--text-h1) leading-(--text-h1--line-height) font-(--font-weight-h1)',
        h2: 'text-(length:--text-h2) leading-(--text-h2--line-height) font-(--font-weight-h2)',
        h3: 'text-(length:--text-h3) leading-(--text-h3--line-height) font-(--font-weight-h3)',
        h4: 'text-(length:--text-h4) leading-(--text-h4--line-height) font-(--font-weight-h4)',
        h5: 'text-(length:--text-h5) leading-(--text-h5--line-height) font-(--font-weight-h5)',
        h6: 'text-(length:--text-h6) leading-(--text-h6--line-height) font-(--font-weight-h6)',
      },
    },
    defaultVariants: { variant: 'h1' },
  },
);

export const textVariants = cva(
  ['self-stretch', 'text-(--color-text-primary)'],
  {
    variants: {
      variant: {
        body: [
          'font-(--font-body)',
          'text-(length:--text-body--line-height)',
          '[text-transform:var(--text-body-transform)]',
          '[font-style:var(--text-body-style)]',
        ],
        paragraph: [
          'font-(--font-paragraph)',
          'text-(length:--text-paragraph--line-height)',
          '[text-transform:var(--text-paragraph-transform)]',
          '[font-style:var(--text-paragraph-style)]',
        ],
        label: [
          'font-(--font-labels)',
          'text-(length:--text-label--line-height)',
          '[text-transform:var(--text-label-transform)]',
          '[font-style:var(--text-label-style)]',
        ],
      },
      weight: { bold: null, semibold: null, medium: null, regular: null },
      size: { sm: null, md: null, lg: null, xl: null, '2xl': null },
    },
    compoundVariants: textCompoundVariants,
    defaultVariants: { variant: 'body', size: 'md', weight: 'regular' },
  },
);
